if (!customElements.get('quick-order-list')) {
  customElements.define(
    'quick-order-list',
    class QuickOrderList extends BulkAdd {
      cartUpdateUnsubscriber = undefined;
      hasPendingQuantityUpdate = false;

      constructor() {
        super();
        this.isListInsideModal = this.closest('bulk-modal, .card-bulk-modal-content');

        this.stickyHeaderElement = document.querySelector('sticky-header');
        if (this.stickyHeaderElement) {
          this.stickyHeader = {
            height: this.stickyHeaderElement.offsetHeight,
            type: this.stickyHeaderElement.getAttribute('data-sticky-type') || '',
          };
        }

        this.totalBar = this.getTotalBar();
        if (this.totalBar) {
          this.totalBarPosition = window.innerHeight - this.totalBar.offsetHeight;
          this.handleResize = this.handleResize.bind(this);
          window.addEventListener('resize', this.handleResize);
        }

        this.querySelector('form')?.addEventListener('submit', (e) => e.preventDefault());

        this._setupDelegatedListeners();
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber?.();
        if (this.dataset.listHidden && !this.closest('.tingle-modal-box')) return;
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
          if (event.source === this.id) return;
          this.toggleTableLoading(true);
          await this.refresh();
          this.toggleTableLoading(false);
        });
        this._setupElementListeners();

        if (this.dataset.page) {
          const pageManager = this.closest('page-manager');
          if (pageManager) {
            if (!this._pageLoadedHandler) {
              this._pageLoadedHandler = () => {
                if (!this.isConnected) return;
                this.dataset.url =
                  window.location.origin + window.location.pathname + window.location.search;
                this.refresh();
              };
            }
            pageManager.addEventListener('pageLoaded', this._pageLoadedHandler);
          }
        }
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        window.removeEventListener('resize', this.handleResize);
        if (this._pageLoadedHandler) {
          this.closest('page-manager')?.removeEventListener('pageLoaded', this._pageLoadedHandler);
        }
      }

      handleResize() {
        this.totalBarPosition = window.innerHeight - (this.totalBar?.offsetHeight || 0);
        if (this.stickyHeader) this.stickyHeader.height = this.stickyHeaderElement?.offsetHeight || 0;
      }

      _setupDelegatedListeners() {
        this._pending = {};
        this._staged = {};
        this._requestInFlight = false;
        this._deferredFlush = false;
        this.commitMode = this.hasAttribute('data-commit-button');
        this._flushDebounced = null;

        const flush = debounce(() => {
          if (this._requestInFlight) {
            this._deferredFlush = true;
            return;
          }
          const items = { ...this._pending };
          this._pending = {};
          if (Object.keys(items).length > 0) this.updateMultipleQty(items);
        }, 500);
        this._flushDebounced = flush;

        this.addEventListener('change', (event) => {
          if (!event.target.classList.contains('quantity__input')) return;
          const input = event.target;
          const id = input.dataset.index;
          const val = parseInt(input.value);

          this.clearRowError(id);

          if (val !== 0 && val < parseInt(input.dataset.min)) {
            this.setValidity(event, id, window.quickOrderListStrings.min_error.replace('[min]', input.dataset.min));
            return;
          }
          if (input.max && val > parseInt(input.max)) {
            this.setValidity(event, id, window.quickOrderListStrings.max_error.replace('[max]', input.max));
            return;
          }
          if (val !== 0 && val % parseInt(input.step) !== 0) {
            this.setValidity(event, id, window.quickOrderListStrings.step_error.replace('[step]', input.step));
            return;
          }

          input.setCustomValidity('');
          input.setAttribute('value', val);

          if (this.commitMode) {
            this._staged[id] = val;
            this.updateOptimisticTotal(input, val);
            this.updateCommitButtonState();
            return;
          }

          this._pending[id] = val;
          this.hasPendingQuantityUpdate = true;
          this.updateOptimisticTotal(input, val);
          flush();
        });

        this.addEventListener('click', (event) => {
          const rowCommitButton = event.target.closest('button[data-commit-row]');
          if (rowCommitButton) {
            event.preventDefault();
            this.commitRow(rowCommitButton.dataset.commitRow);
            return;
          }

          const commitButton = event.target.closest('button[data-commit-button]');
          if (commitButton) {
            event.preventDefault();
            this.commitPending();
            return;
          }

          const removeButton = event.target.closest('.quick-order-list-remove-button');
          if (!removeButton) return;
          event.preventDefault();

          const variantId = removeButton.dataset.index;
          if (this._staged && variantId in this._staged) {
            delete this._staged[variantId];
            this.updateCommitButtonState();
          }
          const bulkInput = this.querySelector(`#Quantity-${variantId}`)?.closest('bulk-quantity-input');
          if (bulkInput && !bulkInput.querySelector('.progress-bar-container')?.classList.contains('hidden')) return;

          const btn = removeButton.querySelector('button');
          btn?.setAttribute('disabled', '');
          removeButton.querySelector('.remove-btn__icon')?.classList.add('hidden');
          removeButton.querySelector('.remove-btn__spinner')?.classList.remove('hidden');
          this.updateMultipleQty({ [variantId]: 0 });
        });
      }

      commitPending() {
        const staged = { ...this._staged };
        this._staged = {};
        this.updateCommitButtonState();
        if (Object.keys(staged).length === 0) return;

        if (this._requestInFlight) {
          this._pending = { ...this._pending, ...staged };
          this._deferredFlush = true;
          return;
        }
        this.updateMultipleQty(staged);
      }

      commitRow(variantId) {
        const id = String(variantId);
        if (!this._staged || !(id in this._staged)) return;

        const staged = { [id]: this._staged[id] };
        delete this._staged[id];
        this.updateCommitButtonState();

        if (this._requestInFlight) {
          this._pending = { ...this._pending, ...staged };
          this._deferredFlush = true;
          return;
        }
        this.updateMultipleQty(staged);
      }

      updateCommitButtonState() {
        if (!this.commitMode) return;
        const stagedIds = Object.keys(this._staged || {});
        const strings = window.quickOrderListStrings || {};
        this.querySelectorAll('button[data-commit-row]').forEach((button) => {
          button.toggleAttribute('disabled', !stagedIds.includes(button.dataset.commitRow));
          const row = this.querySelector(`[data-variant-id="${button.dataset.commitRow}"]`);
          const cartQty = row ? parseInt(row.dataset.cartQty || '0', 10) : 0;
          button.textContent = cartQty > 0
            ? (strings.updateCart || 'Update')
            : (strings.addToCart || 'Add');
        });
        const button = this.querySelector('button[data-commit-button]');
        if (button) button.toggleAttribute('disabled', stagedIds.length === 0);
      }

      _setupElementListeners() {
        this.allInputsArray = Array.from(this.querySelectorAll('input[type="number"]'));

        if (!this.dataset.page) {
          this.querySelectorAll('.pagination__item').forEach((link) => {
            link.addEventListener('click', async (event) => {
              event.preventDefault();
              event.stopPropagation();
              const url = new URL(event.currentTarget.href);
              this.toggleTableLoading(true);
              await this.refresh(url.searchParams.get('page') || '1');
              this.scrollTop();
              this.toggleTableLoading(false);
            });
          });
        }

        this.querySelector('.quick-order-list__contents')?.addEventListener(
          'keyup',
          this.handleScrollIntoView.bind(this)
        );

        this.quickOrderListTable?.addEventListener('keydown', this.handleSwitchVariantOnEnter.bind(this));

        if (this.commitMode) {
          this.updateCommitButtonState();
        }
      }

      get currentPage() {
        if (this.dataset.page) {
          return new URLSearchParams(window.location.search).get('page') || this.dataset.page || '1';
        }
        return this.querySelector('.pagination-wrapper')?.dataset?.page ?? '1';
      }

      get cartVariantsForProduct() {
        return JSON.parse(this.querySelector('[data-cart-contents]')?.innerHTML || '[]');
      }

      get quickOrderListTable() {
        return this.querySelector('.quick-order-list__table');
      }

      getCartQuantityForLine(id) {
        const cartQuantity = super.getCartQuantityForLine(id);
        if (cartQuantity > 0) return cartQuantity;
        return this.cartVariantsForProduct.includes(parseInt(id, 10)) ? 1 : 0;
      }

      validateInput(target) {
        const val = parseInt(target.value);
        const min = parseInt(target.dataset.min);
        const step = parseInt(target.step);
        if (target.max) {
          return val === 0 || (val >= min && val <= parseInt(target.max) && val % step === 0);
        }
        return val === 0 || (val >= min && val % step === 0);
      }

      updateMultipleQty(items) {
        if (this.queue.length === 0) this.hasPendingQuantityUpdate = false;

        const loadingInputs = [];
        Object.keys(items).forEach((id) => {
          const bulkInput = this.querySelector(`#Quantity-${id}`)?.closest('bulk-quantity-input');
          if (bulkInput) {
            this.setInputLoading(bulkInput, true);
            loadingInputs.push(bulkInput);
          }
        });

        this._requestInFlight = true;
        this._deferredFlush = false;

        this.toggleLoading(true);
        const url = this.dataset.url || window.location.pathname;
        const linesUpdate = this.startCartLinesUpdate(items);

        this.updateMessage();
        this.setErrorMessage();

        const oldQtys = {};
        Object.keys(items).forEach((id) => {
          const input = this.querySelector(`#Quantity-${id}`);
          oldQtys[id] = parseInt(input?.dataset.cartQuantity || '0', 10) || 0;
        });

        const sections = this.getSectionsToRender().map(({ section }) => section);
        let sectionsUrl;
        if (this.dataset.page) {
          const parsed = new URL(url, window.location.origin);
          parsed.searchParams.delete('section_id');
          parsed.searchParams.delete('page');
          parsed.searchParams.set('page', this.currentPage);
          // cart AJAX API rejects absolute sections_url ("sections_url must be a relative path")
          sectionsUrl = `${parsed.pathname}${parsed.search}`;
        } else {
          sectionsUrl = `${url}?page=${this.currentPage}`;
        }
        const ids = Object.keys(items);
        const newIds = ids.filter((id) => (oldQtys[id] || 0) === 0 && parseInt(items[id], 10) > 0);
        const updateIds = ids.filter((id) => !newIds.includes(id));

        let request = Promise.resolve(null);
        if (newIds.length > 0) {
          const addBody = JSON.stringify({
            items: newIds.map((id) => {
              const row = this.querySelector(`[data-variant-id="${id}"]`);
              const b2bWindow = row ? row.dataset.b2bWindow : undefined;
              const props = { _added_through_site: 'true' };
              if (b2bWindow !== undefined) {
                props._order_type = (b2bWindow && b2bWindow !== 'immediate') ? 'future' : 'immediate';
              } else if (document.documentElement.getAttribute('data-buyer') === 'b2b') {
                props._order_type = 'immediate';
              }
              return {
                id: parseInt(id, 10),
                quantity: parseInt(items[id], 10),
                properties: props,
              };
            }),
            sections,
            sections_url: sectionsUrl,
          });
          request = request.then(() =>
            fetch(`${window.routes.cart_add_url}`, { ...fetchConfig(), ...{ body: addBody } }).then(async (r) => {
              const parsedState = await r.json();
              if (!r.ok || (parsedState.status && parsedState.description)) {
                throw Object.assign(
                  new Error(parsedState.description || parsedState.message || window.cartStrings.error),
                  { code: 'INVALID' },
                );
              }
              return parsedState;
            }),
          );
        }
        if (updateIds.length > 0) {
          const updates = updateIds.reduce((acc, id) => ({ ...acc, [id]: items[id] }), {});
          const updateBody = JSON.stringify({
            updates,
            sections,
            sections_url: sectionsUrl,
          });
          request = request.then(() =>
            fetch(`${window.routes.cart_update_url}`, { ...fetchConfig(), ...{ body: updateBody } })
              .then((r) => r.json())
              .then((parsedState) => {
                if (parsedState.errors) {
                  throw Object.assign(new Error(parsedState.errors), { code: 'INVALID' });
                }
                return parsedState;
              }),
          );
        }

        request
          .then(async (parsedState) => {
            if (!parsedState) return;
            parsedState = await this.normalizeCartState(parsedState);
            linesUpdate?.resolve(parsedState);
            this.renderSections(parsedState, items);
            if (typeof parsedState.item_count === 'number') {
              this.updateCartCount(parsedState.item_count);
            } else {
              fetch(`${window.routes.cart_url}.js`)
                .then((r) => r.json())
                .then((cart) => this.updateCartCount(cart.item_count))
                .catch((e) => {
                  console.warn('[quick-order-list] cart count refresh failed', e);
                });
            }
            publish(PUB_SUB_EVENTS.cartUpdate, { source: this.id, cartData: parsedState });
            const delta = Object.keys(items).reduce((sum, id) => sum + (items[id] - (oldQtys[id] || 0)), 0);
            if (this.querySelector('[data-row-message]')) {
              const perRow = {};
              Object.keys(items).forEach((id) => {
                const d = items[id] - (oldQtys[id] || 0);
                if (d !== 0) perRow[id] = d;
              });
              this.updateRowMessages(perRow);
            } else {
              this.updateMessage(delta);
            }
            if (delta > 0 && window.__essentialUpsellForceToastOnly) window.$s?.addToCartSuccess?.({ showToastOnly: true });
          })
          .catch((e) => {
            if (e.code !== 'INVALID') console.error(e);
            const msg = e.code === 'INVALID' ? e.message : window.cartStrings.error;
            this.dispatchCartErrorEvent(msg, e.code || 'SERVICE_UNAVAILABLE');
            linesUpdate?.reject(e);
            this.setErrorMessage(msg);
          })
          .finally(() => {
            this._requestInFlight = false;
            this.toggleLoading(false);
            loadingInputs.forEach((el) => this.setInputLoading(el, false));
            this.setRequestStarted(false);

            if (this._deferredFlush) {
              this._deferredFlush = false;
              const deferred = { ...this._pending };
              this._pending = {};
              if (Object.keys(deferred).length > 0) this.updateMultipleQty(deferred);
            }
          });
      }

      getSectionsToRender() {
        const sections = [
          {
            id: this.id,
            section: this.dataset.section,
            selector: '.js-contents',
            sectionType: 'self',
          },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#shopify-section-cart-icon-bubble' },
          {
            id: `quick-order-list-live-region-text-${this.dataset.section}`,
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
        ];

        const drawerOpen =
          document.querySelector('cart-drawer')?.classList.contains('active') ||
          document.querySelector('cart-drawer')?.hasAttribute('open');
        if (drawerOpen) {
          sections.push({ id: 'CartDrawer', selector: '.drawer__inner', section: 'cart-drawer' });
        }

        return sections;
      }

      renderSections(parsedState, updatedItems = {}) {
        const { items, sections } = parsedState;

        this.getSectionsToRender().forEach(({ id, selector, section, sectionType }) => {
          if (sectionType === 'self') {
            if (!sections[section]) return;
            const sectionDoc = new DOMParser().parseFromString(sections[section], 'text/html');

            Object.keys(updatedItems).forEach((variantId) => {
              if (this._pending[variantId] !== undefined) return;
              const currentRow = this.querySelector(`#Variant-${variantId}`);
              if (!currentRow) return;
              const freshRow = sectionDoc.querySelector(`#Variant-${variantId}`);
              if (freshRow) {
                currentRow.innerHTML = freshRow.innerHTML;
                currentRow.dataset.cartQty = freshRow.dataset.cartQty || '0';
              }
            });

            this.allInputsArray = Array.from(this.querySelectorAll('input[type="number"]'));

            this.updateTotalBar(parsedState);
            return;
          }

          const sectionElement = document.getElementById(id);
          if (!sectionElement) return;
          const newSection = new DOMParser()
            .parseFromString(sections[section], 'text/html')
            .querySelector(selector);
          if (!newSection) return;

          if (section === 'cart-drawer') {
            sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', items.length === 0);
            const innerTarget = sectionElement.querySelector(selector);
            if (innerTarget) innerTarget.innerHTML = newSection.innerHTML;
          } else {
            sectionElement.innerHTML = newSection.innerHTML;
          }
        });
      }

      updateTotalBar(parsedState) {
        const allKnownIds = new Set(this.cartVariantsForProduct.map(String));
        const cartItemMap = {};
        (parsedState.items || []).forEach((item) => {
          cartItemMap[String(item.variant_id ?? item.id)] = item;
        });

        let totalQty = 0;
        let totalPrice = 0;

        allKnownIds.forEach((variantId) => {
          const item = cartItemMap[variantId];
          if (item) {
            totalQty += item.quantity;
            totalPrice += item.original_line_price;
          }
        });

        Object.entries(this._pending || {}).forEach(([variantId, qty]) => {
          if (!allKnownIds.has(variantId)) return;
          const item = cartItemMap[variantId];
          if (item) {
            totalQty -= item.quantity;
            totalPrice -= item.original_line_price;
          }
          totalQty += qty;
          const row = this.querySelector(`#Variant-${variantId}`);
          const priceCents = parseInt(row?.dataset.price, 10);
          if (!isNaN(priceCents)) totalPrice += priceCents * qty;
        });

        const totalBar = this.getTotalBar();
        if (!totalBar) return;

        totalBar.querySelector('.quick-order-list__total-items span')?.replaceChildren(
          document.createTextNode(String(totalQty))
        );
        totalBar.querySelector('.totals__subtotal-value')?.replaceChildren(
          document.createTextNode(this.formatMoney(totalPrice))
        );
        totalBar
          .querySelectorAll('quick-order-list-remove-all-button[data-action="confirm"]')
          .forEach((btn) => btn.classList.toggle('hidden', totalQty === 0));
        totalBar
          .querySelectorAll('.variant-remove-total')
          .forEach((el) => el.classList.toggle('variant-remove-total--empty', totalQty === 0));

        const confirmText = totalBar.querySelector('.quick-order-list-total__confirmation .text-body');
        if (confirmText) confirmText.textContent = `Remove all ${totalQty} items from your cart?`;
      }

      updateOptimisticTotal(input, qty) {
        const row = input.closest('tr.variant-item');
        if (!row) return;
        const priceCents = parseInt(row.dataset.price, 10);
        if (isNaN(priceCents)) return;
        row.querySelectorAll('.variant-item__totals .price').forEach((el) => {
          el.textContent = this.formatMoney(priceCents * qty);
        });
      }

      formatMoney(cents) {
        if (typeof window.Shopify?.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
        }
        const amount = (cents / 100).toFixed(2);
        const [whole, decimal] = amount.split('.');
        return '$' + whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + decimal;
      }

      updateCartCount(itemCount) {
        if (typeof itemCount === 'undefined') return;
        document.querySelectorAll('.cart-count').forEach((counter) => {
          if (counter.classList.contains('badge')) {
            counter.textContent = itemCount > 0 ? itemCount : '';
          } else {
            counter.textContent = itemCount > 0 ? `(${itemCount})` : '';
          }
          counter.closest('a.trigger-cart')?.classList.toggle('has-items', itemCount > 0);
        });
      }

      setInputLoading(el, loading) {
        if (!el) return;
        el.querySelector('.progress-bar-container')?.classList.toggle('hidden', !loading);
        el.querySelectorAll('.quantity__button').forEach((btn) => btn.toggleAttribute('disabled', loading));
        const variantId = el.querySelector('.quantity__input')?.dataset.index;
        if (variantId) {
          const removeBtn = this.querySelector(`#Remove-${variantId} button`);
          if (removeBtn) removeBtn.toggleAttribute('disabled', loading);
        }
      }

      toggleLoading(loading, target = this) {
        target.querySelector('#shopping-cart-variant-item-status')?.toggleAttribute('aria-hidden', !loading);
        target
          .querySelectorAll('.variant-remove-total .loading__spinner')
          ?.forEach((s) => s.classList.toggle('hidden', !loading));
        target
          .querySelectorAll('.variant-remove-total')
          ?.forEach((el) => el.classList.toggle('is-loading', loading));
      }

      toggleTableLoading(enable) {
        this.quickOrderListTable?.classList.toggle('quick-order-list__container--disabled', enable);
        this.toggleLoading(enable);
      }

      setErrorMessage(message = null) {
        if (!this.errorMessageTemplate) {
          const tmpl = this.querySelector(`#QuickOrderListErrorTemplate-${this.dataset.section}`);
          if (!tmpl) return;
          this.errorMessageTemplate = tmpl.cloneNode(true);
        }
        this.querySelectorAll('.quick-order-list-error').forEach((el) => {
          el.innerHTML = '';
          if (!message) return;
          const clone = this.errorMessageTemplate.cloneNode(true);
          clone.content.querySelector('.quick-order-list-error-message').innerText = message;
          el.appendChild(clone.content);
        });
      }

      clearRowError(id) {
        this.querySelector(`#Quick-order-list-item-error-desktop-${id}`)
          ?.closest('tr')
          ?.classList.add('hidden');
        const mobileErr = this.querySelector(`#Quick-order-list-item-error-mobile-${id}`);
        if (mobileErr) mobileErr.querySelector('.variant-item__error-text').innerHTML = '';
      }

      updateLiveRegions(id, message) {
        const desktopError = this.querySelector(`#Quick-order-list-item-error-desktop-${id}`);
        if (desktopError) {
          desktopError.querySelector('.variant-item__error-text').innerHTML = message;
          desktopError.closest('tr')?.classList.remove('hidden');
        }
        const mobileError = this.querySelector(`#Quick-order-list-item-error-mobile-${id}`);
        if (mobileError) mobileError.querySelector('.variant-item__error-text').innerHTML = message;

        this.querySelector('#shopping-cart-variant-item-status')?.setAttribute('aria-hidden', true);
        const cartStatus = this.querySelector(
          `#quick-order-list-live-region-text-${this.dataset.section}`
        );
        if (cartStatus) {
          cartStatus.setAttribute('aria-hidden', false);
          setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
        }
      }

      updateRowMessages(deltas = {}) {
        clearTimeout(this._rowMsgTimer);
        this.querySelectorAll('[data-row-message]').forEach((el) => {
          el.classList.add('hidden');
          const text = el.querySelector('.quick-order-list__message-text');
          const icon = el.querySelector('.quick-order-list__message-icon');
          if (text) text.textContent = '';
          if (icon) icon.classList.add('hidden');
        });
        const entries = Object.entries(deltas).filter(([, d]) => d !== 0);
        if (entries.length === 0) return;
        entries.forEach(([id, quantity]) => {
          const isNeg = quantity < 0;
          const abs = Math.abs(quantity);
          const tpl = isNeg
            ? abs === 1
              ? window.quickOrderListStrings.itemRemoved
              : window.quickOrderListStrings.itemsRemoved
            : quantity === 1
            ? window.quickOrderListStrings.itemAdded
            : window.quickOrderListStrings.itemsAdded;
          this.querySelectorAll(`[data-row-message="${id}"]`).forEach((el) => {
            el.classList.remove('hidden');
            const text = el.querySelector('.quick-order-list__message-text');
            const icon = el.querySelector('.quick-order-list__message-icon');
            if (text) text.textContent = tpl.replace('[quantity]', abs);
            if (icon) icon.classList.remove('hidden');
          });
        });
        this._rowMsgTimer = setTimeout(() => this.updateRowMessages(), 3500);
      }

      updateMessage(quantity = null) {
        const messages = this.querySelectorAll('.quick-order-list__message-text');
        const icons = this.querySelectorAll('.quick-order-list__message-icon');

        if (quantity === null || isNaN(quantity) || quantity === 0) {
          messages.forEach((m) => (m.innerHTML = ''));
          icons.forEach((i) => i.classList.add('hidden'));
          return;
        }

        const isNeg = quantity < 0;
        const abs = Math.abs(quantity);
        const tpl = isNeg
          ? abs === 1
            ? window.quickOrderListStrings.itemRemoved
            : window.quickOrderListStrings.itemsRemoved
          : quantity === 1
          ? window.quickOrderListStrings.itemAdded
          : window.quickOrderListStrings.itemsAdded;

        messages.forEach((m) => (m.innerHTML = tpl.replace('[quantity]', abs)));
        icons.forEach((i) => i.classList.remove('hidden'));

        clearTimeout(this._messageTimer);
        this._messageTimer = setTimeout(() => this.updateMessage(), 3500);
      }

      getTotalBar() {
        return this.querySelector('.quick-order-list__total');
      }

      async refresh(pageNumber = null) {
        const url = this.dataset.url || window.location.pathname;
        const page = pageNumber || this.currentPage;
        let fetchUrl;
        if (this.dataset.page) {
          const parsed = new URL(url, window.location.origin);
          parsed.searchParams.delete('section_id');
          parsed.searchParams.delete('page');
          parsed.searchParams.set('section_id', this.dataset.section);
          parsed.searchParams.set('page', page);
          fetchUrl = parsed.toString();
        } else {
          fetchUrl = `${url}?section_id=${this.dataset.section}&page=${page}`;
        }
        return fetch(fetchUrl)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const updated = doc.querySelector(`#quick-order-list, #${this.id}`);
            if (!updated) return;
            this.innerHTML = updated.innerHTML;
            this._setupElementListeners();
          })
          .catch(console.error);
      }

      scrollTop() {
        const { top } = this.getBoundingClientRect();
        if (this.isListInsideModal) {
          this.scrollIntoView();
        } else {
          window.scrollTo({
            top: top + window.scrollY - (this.stickyHeader?.height || 0),
            behavior: 'instant',
          });
        }
      }

      scrollQuickOrderListTable(target) {
        const inputTop = target.getBoundingClientRect().top;
        const inputBottom = target.getBoundingClientRect().bottom;

        if (this.isListInsideModal) {
          const crossesTotal = inputBottom > this.totalBar?.getBoundingClientRect().top;
          const crossesHead =
            inputTop <
            this.querySelector('.quick-order-list__table thead')?.getBoundingClientRect().bottom;
          if (crossesTotal || crossesHead) this.scrollToCenter(target);
        } else {
          const stickyBottom = this.stickyHeaderElement?.getBoundingClientRect().bottom;
          const crossesTotal = inputBottom > this.totalBarPosition;
          const outsideViewport =
            inputBottom < this.querySelector('.variant-item__quantity-wrapper')?.offsetHeight;
          const stickyCovers =
            this.stickyHeaderElement &&
            this.stickyHeader.type !== 'on-scroll-up' &&
            this.stickyHeader.height > inputTop;
          const stickyScrollUpCovers =
            this.stickyHeaderElement &&
            this.stickyHeader.type === 'on-scroll-up' &&
            this.stickyHeader.height > inputTop &&
            stickyBottom > 0;

          if (crossesTotal || outsideViewport || stickyCovers || stickyScrollUpCovers) {
            this.scrollToCenter(target);
          }
        }
      }

      scrollToCenter(target) {
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }

      handleScrollIntoView(event) {
        if ((event.key === 'Tab' || event.key === 'Enter') && this.allInputsArray.length !== 1) {
          this.scrollQuickOrderListTable(event.target);
        }
      }

      handleSwitchVariantOnEnter(event) {
        if (event.key !== 'Enter' || event.target.tagName !== 'INPUT') return;
        event.preventDefault();
        event.target.blur();
        if (this.commitMode) {
          this.commitPending();
          return;
        }
        if (!this.validateInput(event.target) || this.allInputsArray.length <= 1) return;

        const current = this.allInputsArray.indexOf(event.target);
        const offset = event.shiftKey ? -1 : 1;
        const next = (current + offset + this.allInputsArray.length) % this.allInputsArray.length;
        this.allInputsArray[next]?.select();
      }
    }
  );
}

if (!customElements.get('quick-order-list-remove-all-button')) {
  customElements.define(
    'quick-order-list-remove-all-button',
    class QuickOrderListRemoveAllButton extends HTMLElement {
      constructor() {
        super();
        this.quickOrderList = this.closest('quick-order-list');
        this.addEventListener('click', (event) => {
          event.preventDefault();
          const action = this.dataset.action;
          if (action === 'confirm') {
            this.toggleConfirmation(false, true);
          } else if (action === 'remove') {
            if (!this.quickOrderList) return;
            const items = this.quickOrderList.cartVariantsForProduct.reduce(
              (acc, id) => ({ ...acc, [id]: 0 }),
              {}
            );
            this.quickOrderList.updateMultipleQty(items);
            this.toggleConfirmation(true, false);
          } else if (action === 'cancel') {
            this.toggleConfirmation(true, false);
          }
        });
      }

      toggleConfirmation(showConfirmation, showInfo) {
        this.quickOrderList
          ?.querySelector('.quick-order-list-total__confirmation')
          ?.classList.toggle('hidden', showConfirmation);
        this.quickOrderList
          ?.querySelector('.quick-order-list-total__info')
          ?.classList.toggle('hidden', showInfo);
      }
    }
  );
}
